import React, { useState, useMemo } from 'react';
import {
    Stack,
    Text,
    IconButton,
    PrimaryButton,
    DefaultButton,
    SearchBox,
    Selection,
    SelectionMode,
    Dropdown,
    IDropdownOption
} from '@fluentui/react';
import { DetailsListLayoutMode } from '@fluentui/react/lib/DetailsList';
import CustomPanel from 'components/controls/CustomPanel';
import CustomDetailList from 'components/controls/CustomDetailList';
import { DefaultGridConfig } from 'components/controls/CustomDetailList/CustomDetailList';
import { ICustomDetailListConfiguration } from 'components/controls/CustomDetailList/ICustomDetailListConfiguration';
import { IGridItem } from 'components/controls/CustomDetailList/IGridItem';
import { IExtendedColumn } from 'model/IExtendedColumn';
import {
    IBenefit as FAM_IBenefit,
    IBenefitRef as FAM_IBenefitRef,
    IPlan as FAM_IPlan
} from 'model/FlexibleAttributeModel';

interface IBenefitComparisonPanelProps {
    templateBenefits: FAM_IBenefit[];
    templatePlans: FAM_IPlan[];
    currentBenefits: FAM_IBenefit[];
    templateAddedBenefitIds: Set<string>;
    stagedBenefitsCount: number;
    committedTemplateBenefitsCount: number;
    loading: boolean;
    onAddBenefit: (benefit: FAM_IBenefit) => void;
}

const BenefitComparisonPanel: React.FC<IBenefitComparisonPanelProps> = React.memo((props) => {
    const {
        templateBenefits,
        templatePlans,
        currentBenefits,
        templateAddedBenefitIds,
        stagedBenefitsCount,
        committedTemplateBenefitsCount,
        loading,
        onAddBenefit
    } = props;

    // Selected plan filter (undefined = show all)
    const [selectedPlanId, setSelectedPlanId] = useState<string | undefined>(undefined);

    // Reset plan filter when template changes
    React.useEffect(() => {
        setSelectedPlanId(undefined);
    }, [templatePlans]);

    // A key derived from which current benefits are template-sourced.
    // Changes whenever a benefit is added/replaced from the template, forcing
    // the template list to remount and re-evaluate the checkmark / + button.
    const templateListKey = useMemo(() => {
        const ids = currentBenefits
            .filter((b: FAM_IBenefit) => b.bcbs_fromassociationtemplate === true || !!b.bcbs_associationtemplatebenefitid)
            .map((b: FAM_IBenefit) => b.id)
            .sort()
            .join(',');
        return `tpl-list-${ids}`;
    }, [currentBenefits]);

    const currentListKey = useMemo(() => {
        const ids = currentBenefits
            .map((b: FAM_IBenefit) => `${b.id}:${b.bcbs_fromassociationtemplate ? 'T' : 'M'}`)
            .sort()
            .join(',');
        return `cur-list-${ids}`;
    }, [currentBenefits]);

    // Grid configuration for template benefits with 10 items per page
    const [templateGridConfig, setTemplateGridConfig] = useState<ICustomDetailListConfiguration>(() => ({
        ...DefaultGridConfig(),
        pageSize: 10,
        page: 1
    }));

    // Grid configuration for current benefits with 10 items per page
    const [currentGridConfig, setCurrentGridConfig] = useState<ICustomDetailListConfiguration>(() => ({
        ...DefaultGridConfig(),
        pageSize: 10,
        page: 1
    }));

    // Search state
    const [templateSearchText, setTemplateSearchText] = useState('');
    const [currentSearchText, setCurrentSearchText] = useState('');

    // Selection instance for current benefits
    const currentSelection = useMemo(() => new Selection(), []);

    // Column definitions for template benefits
    const templateBenefitColumns: IExtendedColumn[] = React.useMemo(() => [
        {
            key: 'actions',
            name: 'Source',
            fieldName: 'actions',
            minWidth: 48,
            maxWidth: 48,
            isResizable: false,
            onRender: (item: IGridItem<FAM_IBenefit>) => {
                const benefit = item.data!;
                const isAdded = item.isAdded === true;

                return (
                    <IconButton
                        iconProps={{ iconName: isAdded ? 'CheckMark' : 'Add' }}
                        title={isAdded ? 'Already added' : 'Add to configuration'}
                        disabled={isAdded}
                        onClick={() => !isAdded && onAddBenefit(benefit)}
                        styles={{
                            root: { width: '28px', height: '28px' },
                            icon: { fontSize: '11px' }
                        }}
                    />
                );
            }
        },
        {
            key: 'name',
            name: 'Benefit Name',
            fieldName: 'name',
            minWidth: 200,
            maxWidth: 1000,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false
        }
    ], [onAddBenefit]);

    // Column definitions for current configuration benefits
    const currentBenefitColumns: IExtendedColumn[] = React.useMemo(() => [
        {
            key: 'source',
            name: 'Source',
            fieldName: 'source',
            minWidth: 48,
            maxWidth: 48,
            isResizable: false,
            onRender: (item: IGridItem<FAM_IBenefit>) => {
                const isFromTemplate =
                    item.data?.bcbs_fromassociationtemplate === true ||
                    !!(item.data?.bcbs_associationtemplatebenefitid);
                return (
                    <div style={{ display: 'flex', alignItems: 'center', width: '28px', height: '28px' }}>
                        <span
                            title={isFromTemplate ? 'This benefit was added from an Association Template' : 'This benefit was added manually'}
                            style={{
                            background: isFromTemplate ? '#0078d4' : '#f3f2f1',
                            color: isFromTemplate ? '#fff' : '#605e5c',
                            borderRadius: '4px',
                            padding: '2px 6px',
                            fontSize: '11px',
                            fontWeight: 600,
                            whiteSpace: 'nowrap'
                        }}>
                            {isFromTemplate ? 'T' : 'M'}
                        </span>
                    </div>
                );
            }
        },
        {
            key: 'name',
            name: 'Benefit Name',
            fieldName: 'name',
            minWidth: 200,
            maxWidth: 1000,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false
        }
    ], [currentBenefits]);

    // Convert benefits to grid items for template with search filtering
    const templateGridItems: IGridItem<FAM_IBenefit>[] = useMemo(() => {
        let filtered = templateBenefits;

        // Apply plan filter if a specific plan is selected
        if (selectedPlanId) {
            const selectedPlan = templatePlans.find(p => p.id === selectedPlanId);
            if (selectedPlan) {
                const planBenefitIds = new Set(selectedPlan.benefitRefs?.map((r: FAM_IBenefitRef) => r.id) ?? []);
                filtered = filtered.filter(b => planBenefitIds.has(b.id));
            }
        }

        // Apply search filter if search text exists
        if (templateSearchText) {
            const searchLower = templateSearchText.toLowerCase();
            filtered = templateBenefits.filter((benefit) => {
                const nameMatch = benefit.name?.toLowerCase().includes(searchLower);
                return nameMatch;
            });
        }

        // Sort: already-added benefits (check icon) first, then alphabetically
        const sorted = [...filtered].sort((a, b) => {
            const aIsAdded = templateAddedBenefitIds.has(a.id);
            const bIsAdded = templateAddedBenefitIds.has(b.id);
            if (aIsAdded && !bIsAdded) return -1;
            if (!aIsAdded && bIsAdded) return 1;
            return a.name.localeCompare(b.name);
        });

        return sorted.map((benefit, index) => {
            const isAdded = templateAddedBenefitIds.has(benefit.id);
            return {
                key: `${benefit.id}:${isAdded ? 'added' : 'pending'}`,
                name: benefit.name || '',
                data: benefit,
                isAdded,
                _index: index
            };
        });
    }, [templateBenefits, templatePlans, selectedPlanId, templateSearchText, templateAddedBenefitIds]);

    // Calculate paged items for template benefits
    const templateGridItemsPaged: IGridItem<FAM_IBenefit>[] = useMemo(() => {
        const pageSize = templateGridConfig.pageSize;
        const page = templateGridConfig.page;
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return templateGridItems.slice(startIndex, endIndex);
    }, [templateGridItems, templateGridConfig.pageSize, templateGridConfig.page]);

    // Convert benefits to grid items for current with search filtering
    const currentGridItems: IGridItem<FAM_IBenefit>[] = useMemo(() => {
        let filtered = currentBenefits;

        // Apply search filter if search text exists
        if (currentSearchText) {
            const searchLower = currentSearchText.toLowerCase();
            filtered = currentBenefits.filter((benefit) => {
                const nameMatch = benefit.name?.toLowerCase().includes(searchLower);
                return nameMatch;
            });
        }

        // Sort: template benefits first (by persisted flag or ID link), then others alphabetically
        const sorted = [...filtered].sort((a, b) => {
            const aIsTemplate = a.bcbs_fromassociationtemplate === true || !!(a.bcbs_associationtemplatebenefitid);
            const bIsTemplate = b.bcbs_fromassociationtemplate === true || !!(b.bcbs_associationtemplatebenefitid);

            if (aIsTemplate && !bIsTemplate) return -1;
            if (!aIsTemplate && bIsTemplate) return 1;
            return a.name.localeCompare(b.name);
        });

        return sorted.map((benefit, index) => {
            const isFromTemplate = benefit.bcbs_fromassociationtemplate === true || !!(benefit.bcbs_associationtemplatebenefitid);
            return {
                key: `${benefit.id}:${isFromTemplate ? 'T' : 'M'}`,
                name: benefit.name || '',
                data: benefit,
                _index: index
            };
        });
    }, [currentBenefits, currentSearchText]);

    // Calculate paged items for current benefits
    const currentGridItemsPaged: IGridItem<FAM_IBenefit>[] = useMemo(() => {
        const pageSize = currentGridConfig.pageSize;
        const page = currentGridConfig.page;
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return currentGridItems.slice(startIndex, endIndex);
    }, [currentGridItems, currentGridConfig.pageSize, currentGridConfig.page]);

    // Sync Selection objects with items - critical for pagination to work with selection
    React.useEffect(() => {
        currentSelection.setItems(currentGridItems, true);
    }, [currentGridItems, currentSelection]);

    // Memoize the header content renderer
    const renderHeader = React.useCallback(() => (
        <>
            <div style={{ flex: 1 }}>
                <Text variant="xLarge" style={{ fontWeight: 600 }}>
                    Benefit Comparison
                </Text>
                {stagedBenefitsCount > 0 && (
                    <Text variant="small" style={{ marginLeft: '10px', color: '#0078d4' }}>
                        ({stagedBenefitsCount} to add)
                    </Text>
                )}
            </div>
        </>
    ), [stagedBenefitsCount]);

    // Don't check for empty array - let the parent handle that
    // Checking here causes the panel to disappear during Redux updates
    if (!templateBenefits) {
        return null;
    }

    return (
        <CustomPanel
            key="benefitComparison"
            title="Benefit Comparison"
            id="benefitComparison"
            canCollapse={false}
            defaultCollapsed={false}
            onRenderHeaderContent={renderHeader}
        >
            {/* Benefits Lists - Side by Side */}
            <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { marginTop: '20px', alignItems: 'stretch', width: '100%' } }}>
                {/* Template Benefits List - Left */}
                <Stack tokens={{ childrenGap: 8 }} styles={{ root: { flex: '1 1 0', minWidth: 0 } }}>
                    <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                        <Text variant="medium" style={{ fontWeight: 600 }}>
                            Template Benefits ({templateGridItems.length}{templateSearchText ? ` of ${templateBenefits.length}` : ''})
                        </Text>
                    </Stack>
                    {templatePlans.length > 1 && (
                        <Dropdown
                            placeholder="Filter by plan..."
                            selectedKey={selectedPlanId ?? null}
                            options={[
                                { key: '', text: 'All Plans' },
                                ...templatePlans.map(p => ({ key: p.id, text: p.name }))
                            ]}
                            onChange={(_, option?: IDropdownOption) => {
                                setSelectedPlanId(option?.key ? String(option.key) : undefined);
                            }}
                            styles={{ root: { marginBottom: 4 } }}
                        />
                    )}
                    <SearchBox
                        placeholder="Search template benefits..."
                        value={templateSearchText}
                        onChange={(_, newValue) => setTemplateSearchText(newValue || '')}
                        onClear={() => setTemplateSearchText('')}
                        styles={{ root: { marginBottom: 8 } }}
                    />
                    <div style={{ borderBottom: '1px solid #edebe9', marginBottom: '8px' }} />
                    <CustomDetailList
                        key={templateListKey}
                        items={templateGridItems}
                        itemsPaged={templateGridItemsPaged}
                        columns={templateBenefitColumns}
                        gridConfig={[templateGridConfig, setTemplateGridConfig]}
                        compact={true}
                        selectionMode={SelectionMode.none}
                        getKey={(item: IGridItem<FAM_IBenefit>) => item.key as string}
                        styles={{
                            root: { width: '100%' },
                            headerWrapper: { width: '100% !important' },
                            contentWrapper: { width: '100% !important' }
                        }}
                    />
                </Stack>

                {/* Current Configuration Benefits - Right */}
                <Stack tokens={{ childrenGap: 8 }} styles={{ root: { flex: '1 1 0', minWidth: 0 } }}>
                    <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                        <Text variant="medium" style={{ fontWeight: 600 }}>
                            Current Configuration Benefits ({currentGridItems.length}{currentSearchText ? ` of ${currentBenefits.length}` : ''})
                        </Text>
                    </Stack>
                    <SearchBox
                        placeholder="Search current benefits..."
                        value={currentSearchText}
                        onChange={(_, newValue) => setCurrentSearchText(newValue || '')}
                        onClear={() => setCurrentSearchText('')}
                        styles={{ root: { marginBottom: 8 } }}
                    />
                    <div style={{ borderBottom: '1px solid #edebe9', marginBottom: '8px' }} />
                    <CustomDetailList
                        key={currentListKey}
                        items={currentGridItems}
                        itemsPaged={currentGridItemsPaged}
                        columns={currentBenefitColumns}
                        gridConfig={[currentGridConfig, setCurrentGridConfig]}
                        compact={true}
                        selection={currentSelection}
                        selectionMode={SelectionMode.multiple}
                        selectionPreservedOnEmptyClick={true}
                        getKey={(item: IGridItem<FAM_IBenefit>) => item.key as string}
                        styles={{
                            root: { width: '100%' },
                            headerWrapper: { width: '100% !important' },
                            contentWrapper: { width: '100% !important' }
                        }}
                    />
                </Stack>
            </Stack>
        </CustomPanel>
    );
}, (prevProps, nextProps) => {
    // Custom comparison function - only re-render if these specific props change
    return (
        prevProps.templateBenefits === nextProps.templateBenefits &&
        prevProps.templatePlans === nextProps.templatePlans &&
        prevProps.currentBenefits === nextProps.currentBenefits &&
        prevProps.templateAddedBenefitIds === nextProps.templateAddedBenefitIds &&
        prevProps.stagedBenefitsCount === nextProps.stagedBenefitsCount &&
        prevProps.loading === nextProps.loading
    );
});

BenefitComparisonPanel.displayName = 'BenefitComparisonPanel';

export default BenefitComparisonPanel;
